// Surface contact: how drawing tools (pencil, charcoal, pen, marker, eraser,
// smudge, airbrush, imported brushes) touch the document's paper.
//
// One model for every tool, with a profile per medium. The paper's relief is
// the Paper & Surface height field (already flattened by Paper Influence and
// flat in "Visual texture only" mode), normalised to standard deviations, z:
// peaks sit around z = +2, valleys around z = -2. A tool reaches `reach`
// standard deviations below the peaks:
//
//   reach   = base + pressure * pressureReach          (harder press goes deeper)
//   contact = smoothstep(-width, width, reach - (2 - z)) (soft media compress: wider)
//   amount  = mix(1, valleyFloor + (1 - valleyFloor) * contact, strength)
//
// `valleyFloor` is what one pass leaves in the parts it does not reach, so
// repeated passes fill the tooth gradually; `strength` is the paper's tooth
// (preset relief and friction, times Paper Influence) times the medium's
// sensitivity. Deposit, removal (eraser) and dragging (smudge) are all scaled
// by `amount` where the material actually lands, so the paper shapes the
// stroke itself rather than being laid over it.
//
// The GLSL below and contactAmount() are the same formula (previews and tests
// use the JS one).
//
// Papers (not canvases, panels or Smooth Digital) are calibrated separately
// (PAPER_TOOTH_CALIBRATION): the paint simulation's paper relief has grain of
// 20-30 document px plus undulation and pulp clouds, right for paint flow but
// far too coarse as drawing tooth. Drawing tools on paper therefore use a fine
// tooth tile made by the same paper generator at a smaller texture scale,
// high-passed (no undulation or clouds) and repeated, with per-paper amplitude,
// reach, contact softness and valley fill.

import { createSurface } from '../surfaces/PaperModel.js';

export const CONTACT_PEAK = 2;

export const SURFACE_CONTACT_GLSL = `
  uniform sampler2D u_tooth;
  uniform vec2 u_toothResolution;
  uniform float u_toothStrength;
  uniform vec4 u_contact;
  float surfaceContact(vec2 documentPosition, float pressure) {
    if (u_toothStrength <= 0.0005) return 1.0;
    float z = (texture2D(u_tooth, documentPosition / u_toothResolution).r - 0.5) * 6.0;
    float reach = u_contact.x + u_contact.y * clamp(pressure, 0.0, 1.0);
    float touch = smoothstep(-u_contact.z, u_contact.z, reach - (${CONTACT_PEAK.toFixed(1)} - z));
    return mix(1.0, u_contact.w + (1.0 - u_contact.w) * touch, clamp(u_toothStrength, 0.0, 1.0));
  }
`;

const smoothstep = (a, b, x) => {
  const t = Math.max(0, Math.min(1, (x - a) / (b - a)));
  return t * t * (3 - 2 * t);
};

// z: normalised height (standard deviations); profile from contactProfileFor.
export function contactAmount(z, pressure, profile, strength) {
  if (!(strength > 0.0005)) return 1;
  const reach = profile.base + profile.pressureReach * Math.max(0, Math.min(1, pressure));
  const touch = smoothstep(-profile.width, profile.width, reach - (CONTACT_PEAK - z));
  return 1 + ((profile.floor + (1 - profile.floor) * touch) - 1) * Math.max(0, Math.min(1, strength));
}

// Height field (0..1) -> bytes of z / 6 + 0.5, plus its statistics.
export function normaliseToothField(heightField) {
  const n = heightField.length;
  let mean = 0;
  for (let i = 0; i < n; i += 1) mean += heightField[i];
  mean /= Math.max(1, n);
  let variance = 0;
  for (let i = 0; i < n; i += 1) variance += (heightField[i] - mean) ** 2;
  const sd = Math.sqrt(variance / Math.max(1, n));
  const bytes = new Uint8Array(n);
  if (sd < 1e-5) {
    bytes.fill(128);
  } else {
    for (let i = 0; i < n; i += 1) {
      const z = (heightField[i] - mean) / sd;
      bytes[i] = Math.max(0, Math.min(255, Math.round((z / 6 + 0.5) * 255)));
    }
  }
  return { bytes, mean, sd };
}

// How much tooth the paper has for drawing tools: relief and friction of the
// preset (both already scaled by Paper Influence; zero in visual-only mode).
export function paperToothStrength({ tooth = 0, friction = 0, sd = 1 } = {}) {
  if (!(sd > 1e-5)) return 0;
  return Math.max(0, Math.min(1, (0.6 * tooth + 0.4 * friction) / 0.6));
}

// Tooth strength the drawing tools use. Calibrated papers apply their
// amplitude before the limit, so Paper Influence acts over its whole range even
// on the roughest papers; canvases and panels use paperToothStrength as is.
export function drawingToothStrength(paper, calibration) {
  if (!calibration) return paperToothStrength(paper);
  const { tooth = 0, friction = 0, sd = 1 } = paper || {};
  if (!(sd > 1e-5)) return 0;
  return Math.max(0, Math.min(1, ((0.6 * tooth + 0.4 * friction) / 0.6) * calibration.amplitude));
}

// ---------------------------------------------------------------- paper calibration
// scale: paper texture scale of the drawing tooth (1 = the paint relief's grain);
// amplitude: multiplies the paper's tooth strength; reach: added to every tool's
// reach (standard deviations), so ordinary pressure keeps strokes coherent;
// width: multiplies the contact softness (pressure fills the valleys gradually);
// floor: valley share combined with the medium's own (repeated passes even out).
export const PAPER_TOOTH_CALIBRATION = Object.freeze({
  smooth_drawing: { scale: 0.3, amplitude: 0.3, reach: 1.3, width: 1.35, floor: 0.5 },
  bristol: { scale: 0.28, amplitude: 0.22, reach: 1.5, width: 1.35, floor: 0.6 },
  hot_press: { scale: 0.3, amplitude: 0.38, reach: 1.15, width: 1.3, floor: 0.42 },
  cartridge: { scale: 0.34, amplitude: 0.45, reach: 1.0, width: 1.3, floor: 0.36 },
  sketchbook: { scale: 0.34, amplitude: 0.5, reach: 0.95, width: 1.3, floor: 0.32 },
  cold_press: { scale: 0.36, amplitude: 0.6, reach: 0.8, width: 1.25, floor: 0.26 },
  custom: { scale: 0.36, amplitude: 0.6, reach: 0.8, width: 1.25, floor: 0.26 },
  handmade: { scale: 0.4, amplitude: 0.55, reach: 0.7, width: 1.25, floor: 0.22 },
  rough: { scale: 0.42, amplitude: 0.55, reach: 0.55, width: 1.2, floor: 0.18 },
  // Pastel paper keeps its laid lines only as a faint direction: at drawing
  // scale the laid and chain lines otherwise read as a regular grid.
  pastel: { scale: 0.9, amplitude: 0.7, reach: 0.5, width: 1.2, floor: 0.16, laidScale: 0.3 },
});
// Fine tooth tile: document px (one cell per px), repeated across the document.
export const PAPER_TOOTH_TILE = 512;
const PAPER_TOOTH_HIGHPASS = 5;

export function paperCalibration(presetId) {
  return PAPER_TOOTH_CALIBRATION[presetId] || null;
}

// A medium's profile on a calibrated paper (identity for canvases and panels).
export function calibrateProfile(profile, calibration) {
  if (!calibration) return profile;
  return {
    ...profile,
    base: profile.base + calibration.reach,
    width: profile.width * calibration.width,
    // Soft dry media keep more of their own low valley floor, so they still
    // break up at light pressure on rough paper.
    floor: profile.floor + (1 - profile.floor) * calibration.floor * (profile.paperFloorShare ?? 1),
  };
}

// Box blur on a wrapping grid (the tile repeats), radius r.
function wrapBlur(src, w, h, r) {
  const tmp = new Float32Array(w * h);
  const out = new Float32Array(w * h);
  const k = 2 * r + 1;
  const mod = (v, n) => ((v % n) + n) % n;
  for (let y = 0; y < h; y += 1) {
    let sum = 0;
    for (let d = -r; d <= r; d += 1) sum += src[y * w + mod(d, w)];
    for (let x = 0; x < w; x += 1) {
      tmp[y * w + x] = sum / k;
      sum += src[y * w + mod(x + r + 1, w)] - src[y * w + mod(x - r, w)];
    }
  }
  for (let x = 0; x < w; x += 1) {
    let sum = 0;
    for (let d = -r; d <= r; d += 1) sum += tmp[mod(d, h) * w + x];
    for (let y = 0; y < h; y += 1) {
      out[y * w + x] = sum / k;
      sum += tmp[mod(y + r + 1, h) * w + x] - tmp[mod(y - r, h) * w + x];
    }
  }
  return out;
}

const tileCache = new Map();
// Fine drawing tooth for a calibrated paper: the paper generator at the
// calibrated scale, high-passed and normalised. Cached per paper and scale.
export function paperToothTile(presetId, textureScale = 1) {
  const calibration = paperCalibration(presetId);
  if (!calibration) return null;
  const scale = calibration.scale * Math.max(0.25, Math.min(4, Number(textureScale) || 1));
  const key = `${presetId}|${scale.toFixed(4)}`;
  if (tileCache.has(key)) return tileCache.get(key);
  const N = PAPER_TOOTH_TILE;
  const surface = createSurface(N, N, presetId, 11, { textureScale: scale, laidScale: calibration.laidScale });
  // Band-pass: a 3x3 pre-blur removes grain finer than a pixel (it would alias
  // into fixed per-pixel salt), the wide blur removes undulation and clouds.
  const fine = wrapBlur(surface.heightField, N, N, 1);
  const low = wrapBlur(surface.heightField, N, N, PAPER_TOOTH_HIGHPASS);
  const high = new Float32Array(N * N);
  for (let i = 0; i < high.length; i += 1) high[i] = fine[i] - low[i];
  const { bytes } = normaliseToothField(high);
  const tile = { bytes, width: N, height: N };
  if (tileCache.size > 6) tileCache.delete(tileCache.keys().next().value);
  tileCache.set(key, tile);
  return tile;
}

// ---------------------------------------------------------------- media

export const PENCIL_MEDIA = Object.freeze([
  { id: 'graphite', label: 'Graphite pencil' },
  { id: 'mechanical', label: 'Mechanical pencil' },
  { id: 'coloured', label: 'Coloured pencil' },
  { id: 'charcoal', label: 'Charcoal' },
  { id: 'pastel', label: 'Soft pastel' },
  { id: 'chalk', label: 'Chalk' },
  { id: 'crayon', label: 'Wax crayon' },
]);
export const PEN_MEDIA = Object.freeze([
  { id: 'pen', label: 'Ink pen' },
  { id: 'fineliner', label: 'Fineliner' },
  { id: 'brush_pen', label: 'Brush pen' },
  { id: 'marker', label: 'Marker' },
]);
export const ERASER_TYPES = Object.freeze([
  { id: 'soft', label: 'Soft rubber' },
  { id: 'kneaded', label: 'Kneaded' },
  { id: 'vinyl', label: 'Hard vinyl' },
  { id: 'precision', label: 'Precision' },
]);

// base / pressureReach / width are in standard deviations of the paper relief;
// floor is the share a single pass leaves in unreached valleys; sensitivity
// multiplies the paper's tooth strength. opacity and softness adjust the
// tool's own stroke (eraser strength, marker bleed).
const PROFILES = {
  // Dry media: tooth dominates.
  graphite: { base: 0.7, pressureReach: 2.4, width: 0.85, floor: 0.08, sensitivity: 1 },
  mechanical: { base: 0.45, pressureReach: 2.1, width: 0.6, floor: 0.06, sensitivity: 0.9 },
  coloured: { base: 0.5, pressureReach: 2.5, width: 0.8, floor: 0.04, sensitivity: 1 },
  charcoal: { base: 0.25, pressureReach: 2.9, width: 0.75, floor: 0.03, sensitivity: 1.25, paperFloorShare: 0.35 },
  pastel: { base: 0.35, pressureReach: 2.7, width: 0.85, floor: 0.05, sensitivity: 1.2, paperFloorShare: 0.45 },
  chalk: { base: 0.2, pressureReach: 2.5, width: 0.6, floor: 0.03, sensitivity: 1.25, paperFloorShare: 0.35 },
  crayon: { base: 0.1, pressureReach: 2.0, width: 0.4, floor: 0.01, sensitivity: 1.3, paperFloorShare: 0.25 },
  // Liquid media: tracks the surface, slight drag and breakup on rough paper.
  pen: { base: 3.0, pressureReach: 1.5, width: 1.2, floor: 0.45, sensitivity: 0.35 },
  fineliner: { base: 3.4, pressureReach: 1.0, width: 1.0, floor: 0.6, sensitivity: 0.25 },
  brush_pen: { base: 2.4, pressureReach: 2.0, width: 1.0, floor: 0.35, sensitivity: 0.45 },
  marker: { base: 4.5, pressureReach: 0, width: 1.5, floor: 0.8, sensitivity: 0.2, bleed: 22 },
  airbrush: { base: 5, pressureReach: 0, width: 2, floor: 0.85, sensitivity: 0.15 },
  // Removal: lifts from the peaks first; pressure reaches the valleys.
  eraser_soft: { base: 0.8, pressureReach: 2.3, width: 0.6, floor: 0.08, sensitivity: 1 },
  eraser_kneaded: { base: 0.2, pressureReach: 1.8, width: 0.6, floor: 0.02, sensitivity: 1, opacity: 0.5 },
  eraser_vinyl: { base: 1.6, pressureReach: 2.6, width: 0.5, floor: 0.2, sensitivity: 1 },
  eraser_precision: { base: 1.8, pressureReach: 2.4, width: 0.35, floor: 0.25, sensitivity: 1, softness: 0 },
  // Dragging material across the relief.
  smudge: { base: 0.8, pressureReach: 2.0, width: 0.8, floor: 0.15, sensitivity: 1 },
  // Imported brushes: dry-media behaviour of moderate sensitivity.
  imported: { base: 0.8, pressureReach: 2.4, width: 0.6, floor: 0.1, sensitivity: 0.8 },
  none: { base: 9, pressureReach: 0, width: 1, floor: 1, sensitivity: 0 },
};

export function getContactProfile(id) {
  return PROFILES[id] || PROFILES.none;
}

// Tool state -> profile. tool: 'pen' | 'eraser' | 'liquify' | ...; preset:
// pen brush preset ('pen', 'pencil', 'airbrush', ...); media: current
// selections; grade: pencil grade (-1 hard .. +1 soft); tilt: 0 upright .. 1 on its side.
export function contactProfileFor({ tool, preset, imported = false, media = getSurfaceMedia(), grade = 0, tilt = 0, liquifyMode = '' } = {}) {
  if (tool === 'eraser') return { ...PROFILES[`eraser_${media.eraser}`] || PROFILES.eraser_soft, id: `eraser_${media.eraser}` };
  if (tool === 'liquify' || tool === 'smudge') return liquifyMode === 'smudge' || tool === 'smudge' ? { ...PROFILES.smudge, id: 'smudge' } : { ...PROFILES.none, id: 'none' };
  if (tool !== 'pen' && tool !== 'line' && tool !== 'rectangle' && tool !== 'ellipse') return { ...PROFILES.none, id: 'none' };
  if (imported) return { ...PROFILES.imported, id: 'imported' };
  if (preset === 'airbrush') return { ...PROFILES.airbrush, id: 'airbrush' };
  if (preset === 'pencil') {
    const id = media.pencil in PROFILES ? media.pencil : 'graphite';
    const p = { ...PROFILES[id], id };
    // Softer leads fill the tooth more easily; hard leads skim the top.
    const soft = Math.max(-1, Math.min(1, Number(grade) || 0));
    if (id === 'graphite' || id === 'coloured') {
      p.base += 0.45 * soft;
      p.pressureReach += 0.5 * soft;
      p.floor = Math.max(0.01, p.floor + 0.05 * soft);
    }
    // Side shading: the flat side rides on the peaks and shows the paper strongly.
    const t = Math.max(0, Math.min(1, Number(tilt) || 0));
    // Pressure is spread over the whole flat of the lead: it reaches less deep,
    // and the contact stays sharp on the peaks.
    p.base -= 0.9 * t;
    p.pressureReach *= 1 - 0.55 * t;
    p.width *= 1 - 0.2 * t;
    p.sensitivity *= 1 + 0.25 * t;
    return p;
  }
  const id = media.pen in PROFILES ? media.pen : 'pen';
  return { ...PROFILES[id], id };
}

// ---------------------------------------------------------------- media selection (shared UI state)
const MEDIA_KEY = 'voxemera.canvas.surfaceMedia.v1';
const DEFAULT_MEDIA = Object.freeze({ pencil: 'graphite', pen: 'pen', eraser: 'soft' });
let media = (() => {
  try {
    const stored = typeof window !== 'undefined' ? JSON.parse(window.localStorage.getItem(MEDIA_KEY) || 'null') : null;
    return normaliseMedia(stored);
  } catch (error) {
    return { ...DEFAULT_MEDIA };
  }
})();
const listeners = new Set();

function normaliseMedia(value) {
  const v = value && typeof value === 'object' ? value : {};
  const pick = (list, id, fallback) => (list.some((m) => m.id === id) ? id : fallback);
  return {
    pencil: pick(PENCIL_MEDIA, v.pencil, DEFAULT_MEDIA.pencil),
    pen: pick(PEN_MEDIA, v.pen, DEFAULT_MEDIA.pen),
    eraser: pick(ERASER_TYPES, v.eraser, DEFAULT_MEDIA.eraser),
  };
}

export function getSurfaceMedia() {
  return media;
}

export function setSurfaceMedia(patch) {
  media = normaliseMedia({ ...media, ...(patch || {}) });
  try { if (typeof window !== 'undefined') window.localStorage.setItem(MEDIA_KEY, JSON.stringify(media)); } catch (error) { /* storage unavailable */ }
  listeners.forEach((listener) => listener(media));
}

export function subscribeSurfaceMedia(listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

// ---------------------------------------------------------------- current paper (for previews)
// The Canvas core publishes the normalised tooth field it draws with, so the
// tool previews show the same paper.
let currentTooth = null; // { bytes, width, height, strength, absorbency, key }
const toothListeners = new Set();
export function publishToothField(field) {
  currentTooth = field;
  toothListeners.forEach((listener) => listener(field));
}
export function getToothField() {
  return currentTooth;
}
export function subscribeToothField(listener) {
  toothListeners.add(listener);
  return () => toothListeners.delete(listener);
}
